import { BaseException } from '../exceptions/base.exception';
import { captureException, SeverityLevel } from '@sentry/react';

export class ExceptionSerializer {
  static toSentryPayload(
    error: BaseException,
    level: SeverityLevel,
  ): {
    exception: Error;
    context: Parameters<typeof captureException>[1] extends infer L ? L : never;
  } {
    return {
      exception: error,
      context: {
        level,
        tags: {
          exceptionType: error.name,
          ...(error.context?.response?.statusCode && {
            statusCode: error.context?.response?.statusCode,
          }),
          ...(error.context?.request?.method && {
            method: error.context?.request?.method,
          }),
          ...(error.context?.service && {
            service: error.context?.service,
          }),
        },
        extra: {
          ...this.redactSensitiveData(error.context),
        },
      },
    };
  }

  static isSerializableError(error: unknown): error is BaseException {
    return (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      'name' in error
    );
  }

  private static redactSensitiveData(
    context: Record<string, any> = {},
  ): Record<string, any> {
    const redacted = structuredClone(context); // deep copy to avoid mutation

    // Redact Authorization header
    if (redacted?.['request']?.headers?.Authorization) {
      redacted['request'].headers.Authorization =
        ExceptionSerializer.redactAuthorization(
          redacted['request'].headers.Authorization,
        );
    }

    if (redacted?.['request']?.headers?.['X-Api-Key']) {
      redacted['request'].headers['X-Api-Key'] =
        ExceptionSerializer.redactAuthorization(
          redacted['request'].headers['X-Api-Key'],
        );
    }

    if (redacted?.['response']?.response) {
      redacted['response'].response = ExceptionSerializer.redactResponse(
        redacted['response'].response,
      );
    }

    return redacted;
  }

  private static redactAuthorization(authHeader: string): string {
    if (authHeader.toLowerCase().startsWith('bearer ')) {
      return 'Bearer <REDACTED_TOKEN>';
    }
    return '<REDACTED_AUTH>';
  }

  private static redactResponse(response: any) {
    if (
      response?.['success'] === false ||
      typeof response?.['error'] === 'string'
    ) {
      return response;
    }

    if (Array.isArray(response?.['response'])) {
      return `Array of (${response?.['response'].length}) items`;
    }
    if (typeof response?.['response'] === 'object') {
      return `Object with (${Object.entries(response['response']).length}) items`;
    }

    return undefined;
  }

  private static isPossiblySensitive(value: string): boolean {
    return /^[0-9a-fA-F\-]{8,}$/.test(value); // UUID-like
  }
}
